// packages import
import { FC, useState, useEffect } from 'react'
import { Box, DialogActions, Button, Typography, CircularProgress, FormControlLabel, Checkbox } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TextField } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'

// component imports
import { CustomModal } from '../../common/CustomModal'
import { Alert } from '../../common/Alert'

// others
import { DeleteInterestEntriesMutation, useDeleteInterestEntriesMutation } from '../../../../generated'

interface DeleteInterestEntriesModalProps {
  open: boolean
  billId?: number
  clientId?: string
  clientName?: string
  defaultFromDate?: string
  defaultWriteOffInterest?: boolean
  onClose: () => void
  onSuccess: () => void
}

const DeleteInterestEntriesModal: FC<DeleteInterestEntriesModalProps> = ({
  open,
  billId,
  clientId,
  clientName,
  defaultFromDate,
  defaultWriteOffInterest = false,
  onClose,
  onSuccess
}) => {
  const [fromDate, setFromDate] = useState<Dayjs | null>(null)
  const [writeOffInterest, setWriteOffInterest] = useState(false)

  const [deleteEntries, { loading }] = useDeleteInterestEntriesMutation({
    onError: (error: Error) => {
      Alert.error(error.message || 'Failed to delete interest entries')
    },
    onCompleted: (data: DeleteInterestEntriesMutation) => {
      if (data?.deleteInterestEntries?.response?.status === 200) {
        const { deletedCount } = data.deleteInterestEntries
        Alert.success(`${deletedCount} interest ${deletedCount === 1 ? 'entry' : 'entries'} deleted successfully`)
        handleClose()
        onSuccess()
      } else {
        Alert.error(data?.deleteInterestEntries?.response?.message || 'Failed to delete interest entries')
      }
    }
  })

  useEffect(() => {
    if (open) {
      if (defaultFromDate) setFromDate(dayjs(defaultFromDate))
      setWriteOffInterest(defaultWriteOffInterest)
    }
  }, [open, defaultFromDate, defaultWriteOffInterest])

  const handleClose = () => {
    setFromDate(null)
    setWriteOffInterest(false)
    onClose()
  }

  const handleConfirm = () => {
    if (!billId || !clientId || !fromDate?.isValid()) return
    deleteEntries({
      variables: {
        deleteInterestEntriesInput: {
          billId,
          clientId,
          fromDate: fromDate.format('YYYY-MM-DD'),
          writeOffInterest
        }
      }
    })
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <CustomModal title="Delete Interest Entries" isOpen={open} handleClose={handleClose}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Bill #{billId} &nbsp;·&nbsp; {clientName}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            All interest entries from the selected date onwards will be permanently deleted for this bill and client.
          </Typography>

          <DatePicker
            label="Delete entries from"
            value={fromDate}
            onChange={value => setFromDate(value as Dayjs | null)}
            renderInput={params => <TextField {...params} fullWidth size="small" />}
          />

          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={
              <Checkbox
                size="small"
                checked={writeOffInterest}
                onChange={e => setWriteOffInterest(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                Don't charge interest on this matter
              </Typography>
            }
          />
        </Box>

        <DialogActions sx={{ padding: '12px 0 0', gap: 1 }}>
          <Button variant="outlined" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={loading || !fromDate?.isValid()}
            endIcon={loading && <CircularProgress size={16} color="inherit" />}
          >
            Delete
          </Button>
        </DialogActions>
      </CustomModal>
    </LocalizationProvider>
  )
}

export default DeleteInterestEntriesModal
